import Janus from './janus.es.js';

/**
 * Safely parse json string.
 * @param jsonString The json string to parse.
 * @param defaultValue Optional default value on error.
 * @return {null|any}
 */
export function safeJsonParse(jsonString, defaultValue = null) {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    if ( defaultValue !== null ) {
      return defaultValue;
    }
  }
  return defaultValue;
}

/**
 * Stop all tracks for a given stream.
 * @param stream The stream to stop tracks on.
 */
export function stopAllTracks(stream) {
  if (!stream) return;

  try {
    const tracks = stream.getTracks();
    for (let i in tracks) {
      const mst = tracks[i];
      if (mst)
        mst.stop();
    }
  } catch (e) {
  }
}

/**
 * Create a valid sip uri.
 * @param sourceUri The source uri.
 * @param registarDomain The registrar domain.
 * @return {string|*}
 */
export function createValidSipUri(sourceUri, registarDomain) {
  if (sourceUri.indexOf("sip:") !== 0 || sourceUri.indexOf("@") < 0) {
    // The uri to call isn't a valid sip address must create.
    return `sip:${sourceUri}@${registarDomain}`;
  }

  // Leave and use AS IS
  return sourceUri;
}

/**
 * Find the domain from the supplied registrar.
 * @param sipRegistrar The sip registrar. Example sip:voip-b.evolveip.net:5060
 */
export function findRegistrarDomain(sipRegistrar) {
  if (!sipRegistrar) return '';
  const [, domain,] = sipRegistrar.split(':');

  return domain;
}

/**
 * Create a log prefix.
 * @param helperId The optiona helperId.
 * @return {string|string}
 */
export function createLogPrefix({ helperId = '' }) {
  return helperId ? `[Helper #${helperId}]` : '';
}

/**
 * Discover the rtp type.
 * @param result The result to inspect.
 * @return {string}
 */
export function discoverRtpType(result) {
  if (!result) return '';

  let rtpType = "";
  let srtp = result["srtp"];
  if (srtp === "sdes_optional")
    rtpType = " (SDES-SRTP offered)";
  else if (srtp === "sdes_mandatory")
    rtpType = " (SDES-SRTP mandatory)";

  return rtpType;
}

/**
 * Process re-invites.
 * @param jsep The jsep to process.
 * @param helperId The helper id. Optionally.
 * @return {{offerlessInvite: boolean, doVideo: boolean, doAudio: boolean}}
 */
export function processReInvite({ jsep, helperId = '' }) {
  const response = {
    doAudio: true,
    doVideo: false,
    offerlessInvite: false,
    sdp: ''
  }

  const logPrefix = createLogPrefix({ helperId });
  if (jsep) {
    const { sdp = '' } = jsep;
    response.sdp = sdp;
    response.doAudio = (sdp.indexOf("m=audio ") > -1);
    response.doVideo = (sdp.indexOf("m=video ") > -1);
    Janus.debug(`${logPrefix} Audio ${(response.doAudio ? "has" : "has NOT")} been negotiated`);
    Janus.debug(`${logPrefix} Video ${(response.doVideo ? "has" : "has NOT")} been negotiated`);
  }
  else {
    Janus.log(`${logPrefix} This call doesn't contain an offer... we'll need to provide one ourselves`);
    response.offerlessInvite = true;
  }

  return response;
}

/**
 * Process incoming call details.
 * @param jsep The jsep.
 * @param result The result to process.
 * @param helperId The optional helper id.
 * @return {{transfer: string, offerlessInvite: boolean, extra: string, referredBy}}
 */
export function processIncomingCallDetails({ jsep, result, helperId }) {
  const { offerlessInvite } = processReInvite({ jsep, helperId });
  // Is this the result of a transfer?
  let transfer = "";
  let referredBy = result["referred_by"];
  let replaces = result["replaces"];
  if (referredBy && replaces)
    transfer = " (referred by " + referredBy + ", replaces call-ID " + replaces + ")";
  else if (referredBy && !replaces)
    transfer = " (referred by " + referredBy + ")";
  else if (!referredBy && replaces)
    transfer = " (replaces call-ID " + replaces + ")";
  transfer = transfer.replace(new RegExp('<', 'g'), '&lt');
  transfer = transfer.replace(new RegExp('>', 'g'), '&gt');

  let extra = "";
  if (offerlessInvite)
    extra = " (no SDP offer provided)"

  // Any security offered? A missing "srtp" attribute means plain RTP
  const rtpType = discoverRtpType(result);

  return { offerlessInvite, extra, transfer, referredBy, rtpType };
}

/**
 * Process transfer call details.
 * @param result The result.
 * @param helperId The helper id.
 * @return {{replaces: *, referTo, referredBy: (*|string), referId, extra: string}}
 */
export function processTransferCallDetails({ result, helperId }) {
  // We're being asked to transfer the call, ask the user what to do
  let referTo = result["refer_to"];
  let referredBy = result["referred_by"] ? result["referred_by"] : "an unknown party";
  let referId = result["refer_id"];
  let replaces = result["replaces"];
  let extra = ("referred by " + referredBy);
  if (replaces)
    extra += (", replaces call-ID " + replaces);
  extra = extra.replace(new RegExp('<', 'g'), '&lt');
  extra = extra.replace(new RegExp('>', 'g'), '&gt');

  return { referTo, referredBy, replaces, referId, extra };
}

// Re-Export Janus for convenience
export { Janus };
